import type { CashflowProvisionDto } from "@bi/types";
import type { AttentionPoint, IntelSeverity } from "./intelligence/executive";
import type { HeroStatus } from "../components/intelligence/SystemHero";
import { fmtBRL } from "./format";

/**
 * Regras do bloco "Provisão" do Fluxo de Caixa: os números que a tela mostra
 * (com ou sem recorrentes estimados), a fila "O que pede ação" e as opções de
 * mês. Puro, sem React, para ser testado.
 */

type Dto = CashflowProvisionDto;

const MONTHS_SHORT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const SEV_ORDER: Record<IntelSeverity, number> = { critical: 0, warning: 1, info: 2 };
/** Abaixo disso, o lançado do mês é pouco perto da média: a provisão sai otimista (ou pessimista). */
export const LOW_COVERAGE = 0.6;
/** Um cliente com essa fatia do a receber do mês é concentração. */
export const CONCENTRATION_SHARE = 0.4;

const pct = (v: number) => `${Math.round(v * 100)}%`;

/** "YYYY-MM" somando `n` meses. */
export function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number);
  const idx = y! * 12 + (m! - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

/** "dez/26" */
export function monthShort(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return `${MONTHS_SHORT[(m ?? 1) - 1]}/${String(y).slice(2)}`;
}

/** Mês corrente + os próximos `count - 1` meses. */
export function monthOptions(currentMonth: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addMonths(currentMonth, i));
}

/** Os números do mês na leitura escolhida (só lançado, ou lançado + recorrentes estimados). */
export function provisionNumbers(d: Dto, withRecurring: boolean) {
  const rec = withRecurring && d.recurring.available;
  const inflow = d.receivable + (rec ? d.recurring.monthInflow : 0);
  const outflow = d.payable + (rec ? d.recurring.monthOutflow : 0);
  return {
    inflow,
    outflow,
    result: inflow - outflow,
    opening: rec ? d.openingBalanceWithRecurring : d.openingBalance,
    closing: rec ? d.closingBalanceWithRecurring : d.closingBalance,
    weekClosing: d.weeks.map((w) => (rec ? w.closingBalanceWithRecurring : w.closingBalance)),
    estimated: rec,
  };
}

/** A fila "O que pede ação" da provisão, do mais urgente para o menos urgente. */
export function provisionAttention(d: Dto, withRecurring: boolean): AttentionPoint[] {
  const n = provisionNumbers(d, withRecurring);
  const pts: Omit<AttentionPoint, "priority">[] = [];
  const basis = d.recurring.basisMonths.length > 0
    ? `${d.recurring.basisMonths[0]} a ${d.recurring.basisMonths[d.recurring.basisMonths.length - 1]}`
    : "";

  // Semana em que o caixa projetado fica negativo (a primeira que ainda não passou).
  const negIdx = d.weeks.findIndex((w, i) => !w.past && n.weekClosing[i]! < 0);
  if (negIdx >= 0) {
    const w = d.weeks[negIdx]!;
    const lowest = Math.min(...n.weekClosing.filter((_, i) => !d.weeks[i]!.past));
    pts.push({
      id: "semana-negativa", severity: "critical", memory: null,
      title: `Caixa fica negativo na semana de ${w.label}`,
      fact: `${n.estimated ? "Com o lançado e os recorrentes estimados" : "Com o que está lançado"}, o saldo projetado chega a ${fmtBRL(n.weekClosing[negIdx]!)} no fim dessa semana.`,
      impact: `${fmtBRL(lowest)} no pior ponto do mês`,
      action: "Antecipe recebíveis ou negocie o vencimento de pagamentos dessa semana. Os títulos estão na lista abaixo.",
      evidence: d.weeks.map((wk, i) => ({ label: `Saldo em ${wk.to.slice(8, 10)}/${wk.to.slice(5, 7)}`, value: fmtBRL(n.weekClosing[i]!), note: wk.past ? "já passou" : undefined })),
    });
  }

  // Lançado pouco perto da média: a leitura pura sai otimista (saídas) ou pessimista (entradas).
  if (!n.estimated && d.coverage.outflowPct != null && d.coverage.outflowPct < LOW_COVERAGE) {
    pts.push({
      id: "saidas-pouco-lancadas", severity: "warning", memory: null,
      title: "Saídas do mês ainda pouco lançadas",
      fact: `O que está lançado a pagar cobre ${pct(d.coverage.outflowPct)} da média de saídas de ${basis}. Salários, combustível e fretes costumam virar título perto do vencimento.`,
      impact: `${fmtBRL(d.recurring.monthOutflow)} sem título`,
      action: "Ligue \"Incluir recorrentes estimados\" para ver o mês com a média, ou leia este saldo como otimista.",
      evidence: [
        { label: "Lançado a pagar", value: fmtBRL(d.payable + d.realizedOutflow) },
        { label: "Média de saídas", value: fmtBRL(d.recurring.avgOutflow), note: basis },
      ],
    });
  }
  if (!n.estimated && d.coverage.inflowPct != null && d.coverage.inflowPct < LOW_COVERAGE) {
    pts.push({
      id: "entradas-pouco-lancadas", severity: "info", memory: null,
      title: "Entradas do mês ainda pouco lançadas",
      fact: `O que está lançado a receber cobre ${pct(d.coverage.inflowPct)} da média de entradas de ${basis}. Fretes ainda não faturados não aparecem como título.`,
      impact: `${fmtBRL(d.recurring.monthInflow)} sem título`,
      action: "Compare com a carteira de fretes prevista antes de concluir que vai faltar caixa.",
      evidence: [
        { label: "Lançado a receber", value: fmtBRL(d.receivable + d.realizedInflow) },
        { label: "Média de entradas", value: fmtBRL(d.recurring.avgInflow), note: basis },
      ],
    });
  }

  const top = d.topReceivable[0];
  if (top && d.receivable > 0 && top.share >= CONCENTRATION_SHARE && !top.name.startsWith("Outros")) {
    pts.push({
      id: "recebimento-concentrado", severity: "warning", memory: null,
      title: "Recebimento do mês concentrado em um cliente",
      fact: `${top.name} responde por ${pct(top.share)} do que está lançado a receber no mês (${top.count} título${top.count > 1 ? "s" : ""}).`,
      impact: fmtBRL(top.amount),
      action: "Confirme com o cliente as datas desses recebimentos. Um atraso dele muda o mês inteiro.",
      evidence: d.topReceivable.slice(0, 3).map((g) => ({ label: g.name, value: fmtBRL(g.amount), note: pct(g.share) })),
    });
  }

  if (d.overdue.receivable > 0) {
    pts.push({
      id: "vencidos-receber", severity: "warning", memory: null,
      title: "Vencidos a receber fora da provisão",
      fact: d.overdue.receivableCount > 1
        ? `${d.overdue.receivableCount} títulos a receber já venceram e não têm previsão nova. Ficam fora da conta porque não se sabe quando entram.`
        : "1 título a receber já venceu e não tem previsão nova. Fica fora da conta porque não se sabe quando entra.",
      impact: fmtBRL(d.overdue.receivable),
      action: "Cobrança: o que for recebido entra no saldo e melhora a provisão. Uma previsão de recebimento no ERP traz o título de volta para a conta.",
      evidence: [{ label: "Vencidos a receber", value: fmtBRL(d.overdue.receivable), note: `${d.overdue.receivableCount} títulos` }],
    });
  }
  if (d.overdue.payable > 0) {
    pts.push({
      id: "vencidos-pagar", severity: "info", memory: null,
      title: "Vencidos a pagar fora da provisão",
      fact: d.overdue.payableCount > 1
        ? `${d.overdue.payableCount} títulos a pagar já venceram e não têm previsão nova. Se forem pagos no mês, o saldo fica menor que o mostrado.`
        : "1 título a pagar já venceu e não tem previsão nova. Se for pago no mês, o saldo fica menor que o mostrado.",
      impact: fmtBRL(d.overdue.payable),
      action: "Confira se ainda são devidos e lance a previsão de pagamento no ERP.",
      evidence: [{ label: "Vencidos a pagar", value: fmtBRL(d.overdue.payable), note: `${d.overdue.payableCount} títulos` }],
    });
  }
  if (d.blocked.count > 0) {
    pts.push({
      id: "titulos-bloqueados", severity: "info", memory: null,
      title: "Títulos bloqueados no mês",
      fact: `${d.blocked.count} título${d.blocked.count > 1 ? "s" : ""} do mês ${d.blocked.count > 1 ? "estão marcados" : "está marcado"} como bloqueado no ERP e ${d.blocked.count > 1 ? "entram" : "entra"} na conta mesmo assim.`,
      impact: fmtBRL(d.blocked.receivable + d.blocked.payable),
      action: "Confirme se vão ser pagos ou recebidos na data. Na lista de títulos eles aparecem com o selo Bloqueado.",
      evidence: [
        { label: "A receber bloqueado", value: fmtBRL(d.blocked.receivable) },
        { label: "A pagar bloqueado", value: fmtBRL(d.blocked.payable) },
      ],
    });
  }

  return pts
    .map((p, i) => ({ p, i }))
    .sort((a, b) => SEV_ORDER[a.p.severity] - SEV_ORDER[b.p.severity] || a.i - b.i)
    .map(({ p }, i) => ({ ...p, priority: i + 1 }));
}

export function provisionHeroStatus(points: AttentionPoint[]): HeroStatus {
  if (points.some((p) => p.severity === "critical")) return "critical";
  if (points.some((p) => p.severity === "warning")) return "warning";
  return "ok";
}

// ── Memória de cálculo dos cartões da provisão ───────────────────────────────

export type ProvisionCardKey = "receber" | "pagar" | "provisao" | "saldo";

export interface ProvisionMemoryLine {
  label: string;
  value: number;
  hint?: string;
  /** item = parcela; total = resultado (deve bater com o cartão) */
  kind: "item" | "total";
  /** "+" ou "−" na frente do rótulo */
  sign?: "+" | "−";
}

export interface ProvisionMemory {
  key: ProvisionCardKey;
  title: string;
  formula: string;
  lines: ProvisionMemoryLine[];
  /** regras do cálculo, em frases */
  rules: string[];
  /** qual lado mostrar em "Quem compõe" e "Documentos" */
  sides: Array<"receber" | "pagar">;
}

const ddmm = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;

/** Véspera do dia 1 do mês "YYYY-MM", em "dd/mm". */
function dayBefore(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y!, m! - 1, 0));
  return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * Memória de um cartão da provisão: as parcelas que somam o número do cartão,
 * na leitura escolhida (com ou sem recorrentes estimados). O total da memória é
 * o mesmo número do cartão; o teste garante.
 */
export function provisionMemory(d: Dto, withRecurring: boolean, key: ProvisionCardKey): ProvisionMemory {
  const n = provisionNumbers(d, withRecurring);
  const mes = d.monthLabel;
  const basis = d.recurring.basisMonths.length > 0
    ? `${d.recurring.basisMonths[0]} a ${d.recurring.basisMonths[d.recurring.basisMonths.length - 1]}`
    : "";
  const fromLabel = d.isCurrentMonth ? `de hoje (${ddmm(d.today)}) até o fim do mês` : `de 01 ao último dia de ${mes.split(" de ")[0]}`;

  const commonRules = [
    "Entram os títulos em aberto (status ABERTA no ERP) com saldo maior que zero.",
    "Cada título cai na data de previsão de recebimento ou pagamento, quando ela está preenchida no ERP e ainda não passou. Sem previsão, cai no vencimento.",
    "Título vencido e sem previsão nova fica fora de todos os meses, porque não se sabe quando entra ou sai. Ele aparece na linha \"Fora da conta\".",
    "Títulos bloqueados no ERP entram na conta e aparecem com o selo Bloqueado nos documentos.",
  ];
  const recRule = (side: "entradas" | "saídas", avg: number, realized: number) =>
    `Recorrentes estimados: média de ${side} realizadas de ${basis} (${fmtBRL(avg)}) menos o que já está lançado${d.isCurrentMonth ? ` e o que já foi ${side === "entradas" ? "recebido" : "pago"} no mês (${fmtBRL(realized)})` : ""}. Nunca fica abaixo de zero.`;

  const sideLines = (side: "receber" | "pagar"): ProvisionMemoryLine[] => {
    const lanc = side === "receber" ? d.receivable : d.payable;
    const count = side === "receber" ? d.receivableCount : d.payableCount;
    const rec = side === "receber" ? d.recurring.monthInflow : d.recurring.monthOutflow;
    const lines: ProvisionMemoryLine[] = [
      { kind: "item", label: `Títulos ${side === "receber" ? "a receber" : "a pagar"} lançados ${fromLabel}`, value: lanc, hint: `${count} título${count === 1 ? "" : "s"}` },
    ];
    if (n.estimated) lines.push({ kind: "item", sign: "+", label: "Recorrentes estimados", value: rec, hint: `pela média de ${basis}` });
    return lines;
  };

  if (key === "receber" || key === "pagar") {
    const isIn = key === "receber";
    const total = isIn ? n.inflow : n.outflow;
    return {
      key,
      title: isIn ? "A receber no mês" : "A pagar no mês",
      formula: n.estimated ? "Títulos lançados no mês + recorrentes estimados" : "Soma dos títulos em aberto que caem no mês",
      lines: [...sideLines(key), { kind: "total", label: isIn ? "A receber no mês" : "A pagar no mês", value: total }],
      rules: [
        ...commonRules,
        ...(n.estimated ? [recRule(isIn ? "entradas" : "saídas", isIn ? d.recurring.avgInflow : d.recurring.avgOutflow, isIn ? d.realizedInflow : d.realizedOutflow)] : []),
        `Fonte: ${isIn ? "contas a receber (fact_accounts_receivable)" : "contas a pagar (fact_accounts_payable)"}${n.estimated ? ` e ${isIn ? "recebimentos (fact_receipts)" : "pagamentos (fact_payments)"} para a média` : ""}, do último sincronismo com o ERP.`,
      ],
      sides: [key],
    };
  }

  if (key === "provisao") {
    return {
      key,
      title: "Provisão do mês",
      formula: "A receber no mês − a pagar no mês",
      lines: [
        { kind: "item", sign: "+", label: "A receber no mês", value: n.inflow, hint: n.estimated ? `${fmtBRL(d.receivable)} lançado + ${fmtBRL(d.recurring.monthInflow)} estimado` : `${d.receivableCount} títulos` },
        { kind: "item", sign: "−", label: "A pagar no mês", value: n.outflow, hint: n.estimated ? `${fmtBRL(d.payable)} lançado + ${fmtBRL(d.recurring.monthOutflow)} estimado` : `${d.payableCount} títulos` },
        { kind: "total", label: "Provisão do mês", value: n.result },
      ],
      rules: [
        d.isCurrentMonth
          ? `No mês corrente a provisão olha só de hoje até o fim do mês. O que já entrou (${fmtBRL(d.realizedInflow)}) e saiu (${fmtBRL(d.realizedOutflow)}) no mês já está no saldo bancário.`
          : "É o resultado só dentro do mês. O saldo no fim do mês soma também o caixa de hoje e o que cai antes do dia 1.",
        ...commonRules,
        ...(n.estimated ? [recRule("entradas", d.recurring.avgInflow, d.realizedInflow), recRule("saídas", d.recurring.avgOutflow, d.realizedOutflow)] : []),
      ],
      sides: ["receber", "pagar"],
    };
  }

  // saldo no fim do mês
  const lines: ProvisionMemoryLine[] = [{ kind: "item", label: "Saldo bancário hoje", value: d.cashBalance, hint: "soma das contas bancárias no último sincronismo" }];
  if (!d.isCurrentMonth) {
    lines.push({ kind: "item", sign: "+", label: `Títulos lançados de ${ddmm(d.today)} a ${dayBefore(d.month)}`, value: d.openingBalance - d.cashBalance, hint: "a receber menos a pagar antes do mês" });
    if (n.estimated) lines.push({ kind: "item", sign: "+", label: "Recorrentes estimados dos meses no caminho", value: d.openingBalanceWithRecurring - d.openingBalance, hint: "entradas menos saídas estimadas" });
  }
  lines.push(
    { kind: "item", sign: "+", label: "Provisão do mês", value: n.result, hint: "a receber menos a pagar no mês" },
    { kind: "total", label: "Saldo no fim do mês", value: n.closing },
  );
  return {
    key,
    title: "Saldo no fim do mês",
    formula: d.isCurrentMonth ? "Saldo bancário de hoje + provisão do mês" : "Saldo bancário de hoje + o que cai até o dia 1 + provisão do mês",
    lines,
    rules: [
      "O saldo bancário é a posição de hoje. Daqui para frente, só entram títulos em aberto (e os estimados, se ligados).",
      ...commonRules,
      "A projeção do Sinersoft, na tabela do fim da tela, é calculada pelo ERP com outra regra e pode dar outro número.",
    ],
    sides: ["receber", "pagar"],
  };
}
